import React from 'react'
import { cn } from '@/lib/utils'

export type PerformanceTier = 'excellent' | 'good' | 'average' | 'bad' | null

interface TierStyle {
  stroke: string
  text: string
  ring: string
  bg: string
}

const TIER_STYLES: Record<NonNullable<PerformanceTier> | 'empty', TierStyle> = {
  excellent: {
    stroke: 'stroke-emerald-500',
    text: 'text-emerald-600',
    ring: 'ring-emerald-200/60',
    bg: 'bg-emerald-50/60',
  },
  good: {
    stroke: 'stroke-teal-600',
    text: 'text-teal-700',
    ring: 'ring-teal-200/60',
    bg: 'bg-teal-50/50',
  },
  average: {
    stroke: 'stroke-amber-500',
    text: 'text-amber-600',
    ring: 'ring-amber-200/60',
    bg: 'bg-amber-50/50',
  },
  bad: {
    stroke: 'stroke-rose-500',
    text: 'text-rose-600',
    ring: 'ring-rose-200/60',
    bg: 'bg-rose-50/50',
  },
  empty: {
    stroke: 'stroke-slate-300',
    text: 'text-slate-400',
    ring: 'ring-slate-200/60',
    bg: 'bg-slate-50/50',
  },
}

export function tierStyle(tier: PerformanceTier): TierStyle {
  return TIER_STYLES[tier ?? 'empty']
}

interface ArcGaugeProps {
  /** Performance vs STLY as percent (e.g. 87 for 87%). null = no data */
  pct: number | null
  tier: PerformanceTier
  size?: number
  /** Visual cap — arc fills to 100% of its track at this pct. */
  cap?: number
}

/**
 * A calm, refined 270° arc gauge. One filled arc on a faded track.
 * Display only — caller passes pct/tier from the backend; no math here.
 */
export function ArcGauge({ pct, tier, size = 132, cap = 130 }: ArcGaugeProps) {
  const stroke = 6
  const r = (size - stroke) / 2 - 2
  const cx = size / 2
  const cy = size / 2

  // Arc sweep: 270° starting at 135° (bottom-left), ending at 45° (bottom-right).
  const sweepDeg = 270
  const startDeg = 135
  const endDeg = startDeg + sweepDeg // 405° (= 45° normalized)

  const fullCircumference = 2 * Math.PI * r
  const trackLength = (sweepDeg / 360) * fullCircumference
  const fillRatio = pct == null ? 0 : Math.max(0, Math.min(1, pct / cap))
  const fillLength = trackLength * fillRatio
  const dashGap = fullCircumference - trackLength

  const polar = (deg: number) => {
    const rad = (deg * Math.PI) / 180
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
  }

  const start = polar(startDeg)
  const end = polar(endDeg)
  const largeArc = sweepDeg > 180 ? 1 : 0

  // Track path: full 270° arc
  const trackPath = `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`

  const style = tierStyle(tier)

  return (
    <div className={cn('relative inline-flex items-center justify-center')}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="overflow-visible"
        role="img"
        aria-label={pct == null ? 'No comparison data' : `Performance ${pct} percent of last year`}
      >
        {/* faded reference ticks at 70/90/100 to show tier boundaries */}
        {[70, 90, 100].map((mark) => {
          const markRatio = mark / cap
          const markAngle = startDeg + sweepDeg * markRatio
          const inner = polar(markAngle)
          const outerRad = r + 4
          const outerX = cx + outerRad * Math.cos((markAngle * Math.PI) / 180)
          const outerY = cy + outerRad * Math.sin((markAngle * Math.PI) / 180)
          return (
            <line
              key={mark}
              x1={inner.x}
              y1={inner.y}
              x2={outerX}
              y2={outerY}
              className="stroke-slate-300"
              strokeWidth={1}
            />
          )
        })}

        {/* Background track */}
        <path
          d={trackPath}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          className="text-slate-100"
        />

        {/* Filled portion */}
        {pct != null && pct > 0 && (
          <path
            d={trackPath}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${fillLength} ${dashGap + (trackLength - fillLength)}`}
            className={cn(style.stroke, 'transition-[stroke-dasharray] duration-700 ease-out')}
          />
        )}
      </svg>

      {/* Center label */}
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        {pct == null ? (
          <>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              No STLY
            </span>
            <span className="mt-0.5 text-[10px] text-slate-400">data</span>
          </>
        ) : (
          <>
            <span className={cn('font-display text-3xl font-semibold tabular-nums leading-none', style.text)}>
              {pct}
              <span className="text-base font-medium opacity-70">%</span>
            </span>
            <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              vs STLY
            </span>
          </>
        )}
      </div>
    </div>
  )
}
